import { Text, VStack } from "@chakra-ui/layout";
import { Box, Input } from "@chakra-ui/react";
import { ChangeEvent, useRef } from "react";

import { BCADocumentFile, BCAValidDocumentInfo } from "@bcasmart/bca-client-signature/BCADocuments";
import { selectFilesOnDevice } from "@bcasmart/bca-client-signature/BCAQSCD";
import { BCAReturnCode } from "@bcasmart/bca-client-signature/BCAQSCDTypes";
import { $map } from "foundation-ts/array";
import { UUID } from "foundation-ts/types";
import { $count, $isnumber, $isuuid, $keys, $ok } from "foundation-ts/commons";
import { CloudSubscriptionDto, SessionDto, UserDto } from "@bcasmart/doxecure-commons/doxecureClientTypes";
import { UserProfile } from "@bcasmart/doxecure-commons/doxecureTypes";

import { ColumnTitle } from "@components/ColumnTitle/ColumnTitle";
import { G1Button } from "@components/G1Button";
import { UploadFailModal } from "@components/Modals/UploadFailModal";
import { config } from "@/config/config";
import { DocumentsToSignBox } from "@containers/signature/DocumentsToSignBox";
import { SignatureBox } from "@containers/signature/SignatureBox";
import { $g1Color } from "@/theme/G1Style";
import { NeededPersonRole } from "@utils/TypesAndConstants";

// TODO: do we keep openFailModal here ?
interface SignatureCreationPanelProps {
    session: SessionDto;
    userProfile: UserDto;
    officeDocuments?: boolean;
    openFailModal: boolean;
    optionAddApproversSwitchChecked?: boolean;
    optionInvisibleSwitchChecked?: boolean;
    optionOneToManyModeSwitchChecked?: boolean;
    onAddUser: (role: NeededPersonRole) => void;
    onChangeSession: (oldSession:SessionDto, newSession:SessionDto) => void;
    onRemoveUser: (apid: UUID, role: NeededPersonRole) => void;
    onSaveAsDraft: () => void;
    onSelectDrive: (drive: CloudSubscriptionDto) => void;
    onPutToSignature: () => void;
    onToggleAddApproversSwitch: () => void;
    onToggleInvisibleSwitch: () => void;
    onToggleOneToManyModeSwitch: () => void;
    onUploadFiles: (files: FileList|BCADocumentFile[]|string) => void;
    onViewDocument: (apid: UUID) => void;
    setOpenFailModal: (open: boolean) => void;
}

export const SessionCreationPanel = (props: SignatureCreationPanelProps) => {
    const { 
        session, 
        userProfile,
        openFailModal,
        officeDocuments,
        optionAddApproversSwitchChecked,
        optionInvisibleSwitchChecked,
        optionOneToManyModeSwitchChecked,
        onAddUser,
        onRemoveUser,
        onChangeSession,
        onPutToSignature,
        onSaveAsDraft,
        onSelectDrive,
        onToggleAddApproversSwitch,
        onToggleInvisibleSwitch,
        onToggleOneToManyModeSwitch,
        onUploadFiles,
        onViewDocument,
        setOpenFailModal
    } = props;
    const inputFile = useRef<HTMLInputElement>(null) ;
    const isSessionValid = $count(session.documents) > 0 && $isuuid(session.expeditor.apid) && $count(session.signatures) > 0 && session.title.length > 0 ;
  
    const onBrowserUploadFiles = (e: ChangeEvent<HTMLInputElement>) => {
        if ($ok(e.target.files)) {
            onUploadFiles(e.target!.files!);
        }
    }

    const handleClickUploadFiles = async () => {
        const fileTypes = !officeDocuments ? ['.pdf'] : $map($keys(BCAValidDocumentInfo), e => '.'+e);
        const selectRet = await selectFilesOnDevice(fileTypes, true);
        if ($isnumber(selectRet)) {
            if ((selectRet === BCAReturnCode.NotImplemented) && inputFile && inputFile.current !== null) {
                inputFile.current.click();
            }
        } else {
            onUploadFiles(selectRet as BCADocumentFile[]);
        }
    };

    return (
        <>
            <ColumnTitle color={$g1Color('global.write')}>
                <Text>Mise à la signature</Text>
            </ColumnTitle>

            <VStack spacing="5">
                <SignatureBox
                    session={session}
                    displayCalendar={true}
                    optionAddApproversSwitchChecked={optionAddApproversSwitchChecked}
                    optionInvisibleSwitchChecked={optionInvisibleSwitchChecked}
                    optionOneToManyModeSwitchChecked={optionOneToManyModeSwitchChecked}
                    enableAddApproversOption={config.app.nbMaxApprovers !== 0 && (userProfile.userFlags?.profile === UserProfile.AdministratorCorporate || userProfile.userFlags?.profile === UserProfile.UserCorporate)}
                    onAddUser={onAddUser}
                    onRemoveUser={onRemoveUser}
                    onChangeSession={onChangeSession}
                    onToggleAddApproversSwitch={onToggleAddApproversSwitch}
                    onToggleInvisibleSwitch={onToggleInvisibleSwitch}
                    onToggleOneToManyModeSwitch={onToggleOneToManyModeSwitch}
                />
                <DocumentsToSignBox
                    documents={$ok(session?.documents)?session!.documents!:[]}
                    drives={$ok(userProfile?.cloudSubscriptions) ? userProfile!.cloudSubscriptions! : []}
                    onSelectDrive={onSelectDrive}
                    onClickUploadFiles={handleClickUploadFiles}
                    onViewDocument={onViewDocument}
                />
                <Text fontSize="small" width="100%" style={{color:"red"}}><sup>* </sup>Informations obligatoires</Text>
                <Box display="flex" flexDirection={{ base: "column", xl: "row" }} width="100%" gap="10px">
                    <G1Button component="form" variant="alternate" title="Sauvegarder brouillon" width={{ base: "100%", xl: "50%" }} height="60px" marginRight={{ base: "unset", xl: "2%" }} marginBottom={{ base: "5%", xl: "unset" }} rounded="xl" fontSize="lg" disabled={session.title.length === 0} onClick={onSaveAsDraft} />
                    <G1Button component="form" variant="important" title="Mettre en signature" width={{ base: "100%", xl: "50%" }} height="60px" marginLeft={{ base: "unset", xl: "2%" }} rounded="xl" fontSize="lg" disabled={!isSessionValid} onClick={onPutToSignature} />
                </Box>
                <UploadFailModal isOpen={openFailModal} onClose={() => setOpenFailModal(false)} />
                <Input type="file" display="none" ref={inputFile} onChange={onBrowserUploadFiles} accept={!officeDocuments ? '.pdf' : '.'+$keys(BCAValidDocumentInfo).join(',.')} multiple />
            </VStack>
        </>
    );
};